import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import SEO from '../components/SEO';
import { useCart } from '../context/CartContext';
import { useSiteSettings } from '../context/SiteSettingsContext';
import { Button, Skeleton } from '../components/primitives';
import { Icon, ICON_DEFAULTS } from '../utils/icons';
import { getImageUrl } from '../utils/imageUrl';
import { formatPrice } from '../utils/format';
import './Bundles.css';

const isVideo = (url) => /\.(mp4|webm|ogg|mov)(\?|$)/i.test(url || '');

const computeBundleTotals = (bundle) => {
  const items = bundle.items || [];
  const lineTotal = items.reduce((sum, item) => {
    const unit = parseFloat(item.productPrice) || 0;
    const qty = parseInt(item.quantity, 10) || 1;
    if (item.isFreeItem) return sum;
    return sum + unit * qty;
  }, 0);

  let priceAfter = lineTotal;
  if (bundle.type === 'percentage') {
    priceAfter = lineTotal * (1 - (parseFloat(bundle.discountValue) || 0) / 100);
  } else if (bundle.type === 'fixed') {
    priceAfter = Math.max(0, lineTotal - (parseFloat(bundle.discountValue) || 0));
  }

  return {
    lineTotal,
    priceAfter,
    saved: Math.max(0, lineTotal - priceAfter),
  };
};

const BundleCard = ({ bundle, currency, lang, onAdd }) => {
  const { t } = useTranslation();
  const { lineTotal, priceAfter, saved } = computeBundleTotals(bundle);
  const itemCount = (bundle.items || []).reduce((s, i) => s + (parseInt(i.quantity, 10) || 1), 0);
  const previewImage = (bundle.items || []).find((i) => i.productImage && !isVideo(i.productImage))?.productImage;

  return (
    <article className="bundle-card">
      <div className="bundle-card__media">
        {previewImage ? (
          <img
            src={getImageUrl(previewImage, { size: 'thumb' })}
            alt={bundle.name}
            loading="lazy"
            onError={(e) => { e.target.style.display = 'none'; }}
          />
        ) : (
          <div className="bundle-card__placeholder" aria-hidden="true">
            {bundle.name?.[0] || '·'}
          </div>
        )}
      </div>

      <div className="bundle-card__body">
        <header className="bundle-card__head">
          <h3 className="bundle-card__title">{bundle.name}</h3>
          {bundle.description && (
            <p className="bundle-card__desc">{bundle.description}</p>
          )}
        </header>

        <ul className="bundle-card__items" aria-label={t('bundles.items', { count: itemCount })}>
          {(bundle.items || []).slice(0, 4).map((item, i) => (
            <li key={`${item.productId}-${i}`} className="bundle-card__item">
              <Link to={`/products/${item.productSlug}`} className="bundle-card__item-link">
                <span className="bundle-card__item-name">{item.productName}</span>
                <span className="bundle-card__item-qty">×{item.quantity}</span>
                {item.isFreeItem && (
                  <span className="bundle-card__item-tag">{t('common.freeShipping', 'Free')}</span>
                )}
              </Link>
            </li>
          ))}
        </ul>

        <footer className="bundle-card__foot">
          <div className="bundle-card__price">
            {saved > 0 && (
              <span className="bundle-card__price-original" aria-label="original">
                {formatPrice(lineTotal, { lang })} {currency}
              </span>
            )}
            <span className="bundle-card__price-final">
              {formatPrice(priceAfter, { lang })} {currency}
            </span>
            {saved > 0 && (
              <span className="bundle-card__savings">
                {t('bundles.save', { amount: formatPrice(saved, { lang }), currency })}
              </span>
            )}
          </div>
          <Button
            variant="brand"
            size="md"
            onClick={() => onAdd(bundle)}
            startIcon={<Icon.Cart size={ICON_DEFAULTS.size} strokeWidth={ICON_DEFAULTS.strokeWidth} />}
          >
            {t('bundles.addToCart', 'Add bundle to cart')}
          </Button>
        </footer>
      </div>
    </article>
  );
};

const Bundles = () => {
  const { t, i18n } = useTranslation();
  const { addToCart } = useCart();
  const { currency: ctxCurrency, currencyLabel } = useSiteSettings();
  const currency = currencyLabel || ctxCurrency || t('common.currency');

  const [bundles, setBundles] = useState(null);

  useEffect(() => {
    api.get('/api/bundles/active')
      .then((res) => setBundles(Array.isArray(res.data) ? res.data : []))
      .catch(() => setBundles([]));
  }, []);

  const handleAddBundle = (bundle) => {
    (bundle.items || []).forEach((item) => {
      addToCart(
        {
          id: item.productId,
          name: item.productName,
          slug: item.productSlug,
          price: item.productPrice,
          sku: item.productSku || null,
          images: item.productImage ? [item.productImage] : [],
        },
        parseInt(item.quantity, 10) || 1,
      );
    });
  };

  return (
    <div className="bundles-page">
      <SEO title={t('bundles.title')} description={t('bundles.subtitle')} />

      <header className="bundles-hero">
        <h1 className="bundles-title">{t('bundles.title')}</h1>
        <p className="bundles-subtitle">{t('bundles.subtitle')}</p>
      </header>

      {bundles === null && (
        <div className="bundles-loading" role="status" aria-busy="true" aria-live="polite">
          <Skeleton.Block aspectRatio="16 / 9" rounded="md" style={{ marginBottom: 'var(--space-3)' }} />
          <Skeleton.Block aspectRatio="16 / 9" rounded="md" style={{ marginBottom: 'var(--space-3)' }} />
          <Skeleton.Block aspectRatio="16 / 9" rounded="md" />
        </div>
      )}

      {bundles !== null && bundles.length === 0 && (
        <div className="bundles-empty">
          <p>{t('bundles.empty')}</p>
          <Button as="link" to="/products" variant="brand" size="md">
            {t('about.ctaButton', 'Browse the collection')}
          </Button>
        </div>
      )}

      {bundles !== null && bundles.length > 0 && (
        <div className="bundles-grid">
          {bundles.map((b) => (
            <BundleCard
              key={b.id}
              bundle={b}
              currency={currency}
              lang={i18n.language}
              onAdd={handleAddBundle}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default Bundles;
